import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { 
  Users, Plus, Edit, Trash2, Eye, Calendar, CheckCircle, Mail, Phone, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

export default function GestaoVisitadoresPage() {
  const [visitadores, setVisitadores] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedVisitador, setSelectedVisitador] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: '', email: '', telefone: '', especialidades: [], distritos_atuacao: []
  });

  const ESPECIALIDADES = [
    'Gestão Organizacional', 'Recursos Humanos', 'Qualidade dos Serviços',
    'Gestão Financeira', 'Infraestruturas', 'Impacto Social'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [visitadoresRes, distritosRes] = await Promise.all([
        apiClient.get('/visitare/visitadores'),
        apiClient.get('/distritos')
      ]);
      setVisitadores(visitadoresRes.data);
      setDistritos(distritosRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.email) {
      toast.error('Preencha nome e email');
      return;
    }
    try {
      if (editMode && selectedVisitador) {
        await apiClient.put(`/visitare/visitadores/${selectedVisitador.id}`, formData);
        toast.success('Visitador atualizado');
        setModalOpen(false);
      } else {
        const response = await apiClient.post('/visitare/visitadores', formData);
        setTempPassword(response.data.temp_password);
        toast.success('Visitador criado com sucesso');
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Desativar este visitador?')) return;
    try {
      await apiClient.delete(`/visitare/visitadores/${id}`);
      toast.success('Visitador desativado');
      loadData();
    } catch (error) {
      toast.error('Erro ao desativar');
    }
  };

  const openModal = (visitador = null) => {
    if (visitador) {
      setFormData({
        nome: visitador.nome,
        email: visitador.email,
        telefone: visitador.telefone || '',
        especialidades: visitador.especialidades || [],
        distritos_atuacao: visitador.distritos_atuacao || []
      });
      setSelectedVisitador(visitador);
      setEditMode(true);
    } else {
      setFormData({ nome: '', email: '', telefone: '', especialidades: [], distritos_atuacao: [] });
      setSelectedVisitador(null);
      setEditMode(false);
    }
    setTempPassword(null);
    setModalOpen(true);
  };

  const toggleEspecialidade = (esp) => {
    setFormData(prev => ({
      ...prev,
      especialidades: prev.especialidades.includes(esp)
        ? prev.especialidades.filter(e => e !== esp)
        : [...prev.especialidades, esp]
    }));
  };

  const toggleDistrito = (distId) => {
    setFormData(prev => ({
      ...prev,
      distritos_atuacao: prev.distritos_atuacao.includes(distId)
        ? prev.distritos_atuacao.filter(d => d !== distId)
        : [...prev.distritos_atuacao, distId]
    }));
  };

  const getDistritoNome = (id) => distritos.find(d => d.id === id)?.nome || id;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestao-visitadores-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Visitadores</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Gerir visitadores/visitadores do Portal Visitare</p>
        </div>
        <Button onClick={() => openModal()} className="bg-[#0066CC] hover:bg-[#0052a3]">
          <Plus className="w-4 h-4 mr-2" />
          Novo Visitador
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Visitadores</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">{visitadores.length}</p>
              </div>
              <Users className="w-10 h-10 text-[#0066CC] opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ativos</p>
                <p className="text-3xl font-bold text-green-600">{visitadores.filter(a => a.status === 'ativo').length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Visitas Pendentes</p>
                <p className="text-3xl font-bold text-amber-600">{visitadores.reduce((sum, a) => sum + (a.visitas_pendentes || 0), 0)}</p>
              </div>
              <Calendar className="w-10 h-10 text-amber-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Especialidades</TableHead>
                <TableHead className="text-center">Visitas</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visitadores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    Nenhum visitador registado
                  </TableCell>
                </TableRow>
              ) : (
                visitadores.map(visitador => (
                  <TableRow key={visitador.id}>
                    <TableCell className="font-medium">{visitador.nome}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Mail className="w-3 h-3" />
                        {visitador.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {visitador.telefone && (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-3 h-3" />
                          {visitador.telefone}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {visitador.especialidades?.slice(0, 2).map(esp => (
                          <Badge key={esp} variant="outline" className="text-xs">{esp.slice(0, 10)}...</Badge>
                        ))}
                        {(visitador.especialidades?.length || 0) > 2 && (
                          <Badge variant="outline" className="text-xs">+{visitador.especialidades.length - 2}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-bold">{visitador.total_visitas || 0}</span>
                        <span className="text-xs text-amber-600">{visitador.visitas_pendentes || 0} pend.</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={visitador.status === 'ativo' ? 'default' : 'secondary'}>
                        {visitador.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedVisitador(visitador); setDetailModalOpen(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openModal(visitador)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {visitador.status === 'ativo' && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeactivate(visitador.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Editar Visitador' : 'Novo Visitador'}</DialogTitle>
            <DialogDescription>
              {!editMode && 'Uma password temporária será gerada para o primeiro acesso.'}
            </DialogDescription>
          </DialogHeader>
          
          {tempPassword ? (
            <div className="py-6">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="font-bold text-lg mb-2">Visitador Criado</h3>
                <p className="text-sm text-slate-600 mb-4">Partilhe esta password temporária:</p>
                <div className="p-3 bg-white dark:bg-slate-800 rounded border font-mono text-lg">
                  {tempPassword}
                </div>
                <p className="text-xs text-slate-500 mt-3">O visitador deve alterar a password no primeiro login.</p>
              </div>
              <div className="mt-4 text-center">
                <Button onClick={() => { setTempPassword(null); setModalOpen(false); }}>Fechar</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Nome *</Label>
                  <Input value={formData.nome} onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))} placeholder="Nome completo" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="email@exemplo.pt" disabled={editMode} />
                  </div>
                  <div>
                    <Label>Telefone</Label>
                    <Input value={formData.telefone} onChange={e => setFormData(p => ({ ...p, telefone: e.target.value }))} placeholder="912345678" />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Especialidades</Label>
                  <div className="flex flex-wrap gap-2">
                    {ESPECIALIDADES.map(esp => (
                      <Badge
                        key={esp}
                        variant={formData.especialidades.includes(esp) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleEspecialidade(esp)}
                      >
                        {esp}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Distritos de Atuação</Label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {distritos.map(dist => (
                      <Badge
                        key={dist.id}
                        variant={formData.distritos_atuacao.includes(dist.id) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleDistrito(dist.id)}
                      >
                        {dist.nome}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                <Button onClick={handleSave} className="bg-[#0066CC]">{editMode ? 'Guardar' : 'Criar'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedVisitador?.nome}</DialogTitle>
          </DialogHeader>
          {selectedVisitador && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {selectedVisitador.email}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Telefone</p>
                  <p className="font-medium flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {selectedVisitador.telefone || '-'}
                  </p>
                </div>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Especialidades</p>
                <div className="flex flex-wrap gap-1">
                  {selectedVisitador.especialidades?.map(esp => (
                    <Badge key={esp} variant="outline">{esp}</Badge>
                  )) || <span className="text-slate-500">Nenhuma</span>}
                </div>
              </div>
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Distritos de Atuação</p>
                <div className="flex flex-wrap gap-1">
                  {selectedVisitador.distritos_atuacao?.map(d => (
                    <Badge key={d} variant="outline" className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {getDistritoNome(d)}
                    </Badge>
                  )) || <span className="text-slate-500">Nenhum</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 border rounded-lg text-center">
                  <p className="text-2xl font-bold text-[#0066CC]">{selectedVisitador.total_visitas || 0}</p>
                  <p className="text-xs text-slate-500">Total Visitas</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{selectedVisitador.visitas_pendentes || 0}</p>
                  <p className="text-xs text-slate-500">Pendentes</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
